import { useEffect, useState } from "react";
import { Mail, Clock, ArrowRight } from "lucide-react";
import { companyInfo, images } from "../data/mock";

const SITE_NAME = "Molentis S.A.";

// Applies noindex meta + title without pulling the full SEO hook (maintenance must NOT be indexed)
const applyMaintenanceMeta = () => {
  document.title = `Scheduled Maintenance — ${SITE_NAME}`;
  const setMeta = (name, content) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", name);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };
  setMeta("robots", "noindex, nofollow");
  setMeta(
    "description",
    "Molentis S.A. is currently undergoing scheduled maintenance. Our team will be back online shortly."
  );
};

const Maintenance = () => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    applyMaintenanceMeta();
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeString = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Luxembourg",
  });

  return (
    <main
      data-testid="maintenance-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A1628] text-white"
    >
      {/* Background image + overlays */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${images.luxSkyline})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/85 via-[#0A1628]/70 to-[#0A1628]"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(201,169,97,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 py-20 sm:px-10">
        <div className="w-full max-w-3xl text-center">
          {/* Logo */}
          <img
            data-testid="maintenance-brand-logo"
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_bronti-redesign/artifacts/0gnn98uz_0a2ebb51-027e-4e01-8018-3714f9c86ac3.png"
            alt="Molentis S.A"
            className="mx-auto h-24 sm:h-32 lg:h-40 w-auto object-contain"
          />
          <div className="mx-auto mt-4 h-px w-24 bg-[#C9A961]/60" />

          {/* Status tag */}
          <div
            data-testid="maintenance-status"
            className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/5 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#C9A961]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#C9A961] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#C9A961]" />
            </span>
            Scheduled maintenance
          </div>

          {/* Headline */}
          <h1
            data-testid="maintenance-title"
            className="font-display mt-8 text-4xl leading-tight text-white sm:text-5xl lg:text-6xl"
          >
            We are polishing
            <span className="block italic text-[#C9A961]">every detail.</span>
          </h1>

          {/* Copy */}
          <p
            data-testid="maintenance-description"
            className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
          >
            Our website is temporarily offline for scheduled improvements.
            The Molentis team remains fully available and will resume online
            services shortly. Thank you for your patience and discretion.
          </p>

          {/* Meta strip */}
          <div className="mx-auto mt-12 grid max-w-xl grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-5 text-left backdrop-blur-sm">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">
                <Clock className="h-3.5 w-3.5" />
                Luxembourg time
              </div>
              <div
                data-testid="maintenance-time"
                className="font-display mt-2 text-2xl text-white tabular-nums"
              >
                {timeString}
              </div>
              <div className="mt-1 text-xs text-white/50">
                {companyInfo.hours}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-5 text-left backdrop-blur-sm">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">
                <Mail className="h-3.5 w-3.5" />
                Direct contact
              </div>
              <a
                data-testid="maintenance-email-link"
                href={`mailto:${companyInfo.email}`}
                className="font-display mt-2 block truncate text-base text-white transition-colors hover:text-[#C9A961] sm:text-lg"
              >
                {companyInfo.email}
              </a>
              <div className="mt-1 text-xs text-white/50">
                Reply within one business day
              </div>
            </div>
          </div>

          {/* CTA */}
          <a
            data-testid="maintenance-email-cta"
            href={`mailto:${companyInfo.email}?subject=Enquiry%20while%20site%20is%20offline`}
            className="mt-12 inline-flex items-center gap-3 rounded-full bg-[#C9A961] px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#0A1628] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#d9ba75]"
          >
            Write to our team
            <ArrowRight className="h-4 w-4" />
          </a>

          {/* Footer */}
          <div
            data-testid="maintenance-footer"
            className="mt-16 text-[10px] uppercase tracking-[0.3em] text-white/40"
          >
            © {new Date().getFullYear()} {SITE_NAME} · {companyInfo.jurisdiction}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Maintenance;
